import React, { useState, useRef } from "react";
import {
  Stack,
  Typography,
  Checkbox,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Box,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import { TASK_PRIORITIES, TASK_CATEGORIES } from "../../constants/clinicalDashboard";

// Category color map
const CATEGORY_COLORS = {
  care_gap:       { bg: "#DBEAFE", text: "#1E40AF" },
  coding_review:  { bg: "#F3E8FF", text: "#6B21A8" },
  follow_up:      { bg: "#FEE2E2", text: "#991B1B" },
  patient_recall: { bg: "#FFEDD5", text: "#9A3412" },
  clinical:       { bg: "#DCFCE7", text: "#166534" },
  care_plan:      { bg: "#DBEAFE", text: "#1E40AF" },
  outreach_call:  { bg: "#FEF3C7", text: "#92400E" },
  appointment:    { bg: "#E0E7FF", text: "#3730A3" },
  administrative: { bg: "#F1F5F9", text: "#475569" },
  other:          { bg: "#F1F5F9", text: "#475569" },
};

/**
 * Item individual de tarea con checkbox, priority dot, título, due date, category chip.
 */
export default function TaskItem({ task, onComplete, onCancel, onDelete, onEdit }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [completing, setCompleting] = useState(false);
  const timerRef = useRef(null);

  const priority = TASK_PRIORITIES.find((p) => p.key === task.priority);
  const category = TASK_CATEGORIES.find((c) => c.key === task.category);
  const catColors = CATEGORY_COLORS[task.category] || CATEGORY_COLORS.other;

  const isCompleted = task.status === "completed";
  const isCancelled = task.status === "cancelled";
  const isDone = isCompleted || isCancelled;

  const today = new Date().toISOString().split("T")[0];
  const isOverdue = !isDone && task.dueDate && task.dueDate < today;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1.5,
        bgcolor: isOverdue ? "#FEF2F2" : isDone || completing ? "#F8FAFC" : "transparent",
        opacity: isDone ? 0.6 : completing ? 0.4 : 1,
        transform: completing ? "scale(0.98)" : "scale(1)",
        transition: "all 400ms ease",
      }}
    >
      {/* Checkbox */}
      <Checkbox
        checked={isCompleted || completing}
        disabled={isDone}
        onChange={() => {
          setCompleting(true);
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            onComplete?.(task.id);
          }, 400);
        }}
        size="small"
        sx={{
          p: 0.25,
          color: priority?.color || "#94A3B8",
          "&.Mui-checked": { color: "#16A34A" },
        }}
      />

      {/* Priority dot */}
      <FiberManualRecordIcon
        sx={{ fontSize: 8, color: priority?.color || "#94A3B8", flexShrink: 0 }}
      />

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.76rem",
            fontWeight: 600,
            textDecoration: isCompleted || completing ? "line-through" : "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {task.title}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
          {/* Category chip */}
          <Chip
            label={category?.label || task.category}
            size="small"
            sx={{
              height: 16,
              fontSize: "0.58rem",
              fontWeight: 600,
              bgcolor: catColors.bg,
              color: catColors.text,
            }}
          />
          {/* Due date */}
          {task.dueDate && (
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.62rem",
                color: isOverdue ? "#DC2626" : "#5A6B7F",
                fontWeight: isOverdue ? 700 : 400,
              }}
            >
              {task.dueDate === today ? "Today" : task.dueDate}
            </Typography>
          )}
          {/* Patient name */}
          {task.patientName && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }} noWrap>
              {task.patientName}
            </Typography>
          )}
          {/* Assignee name */}
          {task.assigneeName && (
            <Typography variant="caption" sx={{ fontSize: "0.58rem", color: "#6366F1", fontWeight: 500 }} noWrap>
              → {task.assigneeName}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Menu */}
      {!isDone && (
        <>
          <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.25 }}>
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            slotProps={{ paper: { sx: { minWidth: 120 } } }}
          >
            <MenuItem
              onClick={() => { setAnchorEl(null); onEdit?.(task); }}
              sx={{ fontSize: "0.8rem" }}
            >
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => { setAnchorEl(null); onCancel?.(task.id); }}
              sx={{ fontSize: "0.8rem" }}
            >
              Cancel
            </MenuItem>
            <MenuItem
              onClick={() => { setAnchorEl(null); onDelete?.(task.id); }}
              sx={{ fontSize: "0.8rem", color: "#DC2626" }}
            >
              Delete
            </MenuItem>
          </Menu>
        </>
      )}
    </Stack>
  );
}
